import { alertDialog, confirmDialog } from '@adornis/dialog/dialog-functions.js';
import type { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { BaseError } from '@adornis/validation/errors/BaseError.js';
import { html } from 'lit';
import { JobTicketWrapper } from '../../../db/job-ticket-wrapper.js';
import { Role } from '../../../permissions/roles.js';
import { TicketAPI } from '../../../ticket/api/TicketAPI.js';
import { displayMailError } from '../../client-utils.js';
import { SgSnackbar } from '../ui/sg-snackbar.js';
import { isSameMonthValue, toPlainDate } from '../../../db/utils/jobTicketDates.js';
import type { JobTicketPerson } from './jobTicketPerson.js';
import {
  collectValidationMessages,
  type JobTicketFormData,
  type PersistedJobTicketDates,
} from '../../../db/utils/jobTicketValidator.js';

/**
 * Writes the form document back onto the person.
 *
 * The form document hands out *proxied* `Date`s whose `valueOf()` throws, so every date is copied
 * into a plain one (see `toPlainDate`) before it reaches the entity constructor.
 */
export const saveJobTicketData = async (personID: string, data: JobTicketFormData): Promise<void> => {
  const jobTicketWrapper = new JobTicketWrapper({
    ...data,
    geburtsDatum: toPlainDate(data.geburtsDatum),
    jobTicketBestellDatum: toPlainDate(data.jobTicketBestellDatum),
    jobTicketKuendigungsDatum: toPlainDate(data.jobTicketKuendigungsDatum),
    jobTicketExportiertAm: toPlainDate(data.jobTicketExportiertAm),
  });

  const saved = await TicketAPI.save(personID, jobTicketWrapper)();
  if (!saved) throw new ExpectedError('Es gab einen Fehler beim Speichern der Daten');
};

/** Sends the invitation mail that lets the employee fill in the ticket form themselves. */
export const sendTicketInvite = async (person: JobTicketPerson): Promise<void> => {
  const confirmed = await confirmDialog('Sind Sie sicher, dass sie eine Einladungsmail senden möchten?');
  if (!confirmed) return;

  try {
    if (!person.roles.find(role => role.name === Role.TICKET))
      throw new ExpectedError('Diesem Mitarbeiter wurde noch nicht die Rolle "Ticket" gegeben.');

    await TicketAPI.invite(person._id)();
    await SgSnackbar.show('Die Einladungsmail wurde versandt.');
  } catch (e) {
    displayMailError(e as BaseError);
  }
};

/**
 * Asks for confirmation before ordering. An already exported ticket whose validity period is being
 * changed gets an additional warning.
 */
export const confirmTicketOrder = async (
  data: JobTicketFormData,
  persisted: PersistedJobTicketDates,
): Promise<boolean> => {
  const validityChanged =
    !isSameMonthValue(data.jobTicketBestellDatum, persisted.orderDate) ||
    !isSameMonthValue(data.jobTicketKuendigungsDatum, persisted.cancelDate);

  if (data.jobTicketExportiertAm && validityChanged) {
    const confirmedChange = await confirmDialog(html`
      <div class="flex flex-col gap-2">
        <p>Das Ticket wurde in der Vergangenheit bereits exportiert.</p>
        <p>Sind Sie wirklich sicher, dass sie den Gültigkeitszeitraum ändern möchten?</p>
        <p>
          Bitte bedenken Sie auch, dass bei einer Neubestellung der "gültig Ab" - Zeitpunkt frühstens der nächste Monat
          sein kann!
        </p>
      </div>
    `);
    if (!confirmedChange) return false;
  }

  return confirmDialog('Sind Sie sicher, dass Sie das Job-Ticket bestellen möchten?');
};

/** Shows the validation errors of the form, resolving their translation keys. */
export const showJobTicketValidationErrors = async (
  error: ValidationError | MultipleValidationError,
): Promise<void> => {
  const messages = collectValidationMessages(error).filter(Boolean);

  await alertDialog(html`
    <div class="flex flex-col gap-2">
      <p class="font-semibold">Bitte überprüfen Sie Ihre Eingaben.</p>
      <ul class="list-disc pl-4">
        ${messages.map(message => html`<li>${message}</li>`)}
      </ul>
    </div>
  `);
};
